import { useEffect } from "react";
import { BrowserRouter, Route, Routes, Navigate } from "react-router-dom";
import { ToastContainer } from 'react-toastify';
import { ApplicationProvider } from "./context/ApplicationContext";
import { applyTheme } from "./helper/themeHelper";
import RouteScrollToTop from "./helper/RouteScrollToTop";
import LandingPage from "./pages/LandingPage";
import ViewProfilePage from "./pages/ViewProfilePage";
import ViewDetailsPage from "./pages/ViewDetailsPage";
import ThemePage from "./pages/ThemePage";

import HomePageOne from "./pages/HomePageOne";
import ControlTowerPage from "./pages/ControlTowerPage";
import AuditLogsPage from "./pages/AuditLogsPage";
import ContactMessagesPage from "./pages/ContactMessagesPage";
import MobileContentPage from "./pages/MobileContentPage";
import SupportTicketsPage from "./pages/SupportTicketsPage";
import ReservationsPage from "./pages/ReservationsPage";
import VenueVerificationPage from "./pages/VenueVerificationPage";
import SubscriptionControlPage from "./pages/SubscriptionControlPage";
import UserManagementPage from "./pages/UserManagementPage";
import ContentModerationPage from "./pages/ContentModerationPage";
import TosPage from "./pages/TosPage";
import BusinessPrivacyPage from "./pages/BusinessPrivacyPage";
import ContactUsPage from "./pages/ContactUsPage";
import PublicTosPage from "./pages/PublicTosPage";
import PublicPrivacyPage from "./pages/PublicPrivacyPage";

import HomePageTwo from "./pages/HomePageTwo";
import HomePageThree from "./pages/HomePageThree";
import HomePageFour from "./pages/HomePageFour";
import HomePageFive from "./pages/HomePageFive";
import HomePageSix from "./pages/HomePageSix";
import HomePageSeven from "./pages/HomePageSeven";
import CalendarMainPage from "./pages/CalendarMainPage";
import MapsPage from "./pages/MapsPage";
import ErrorPage from "./pages/ErrorPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import SignInPage from "./pages/SignInPage";
import SignUpPage from "./pages/SignUpPage";
import NotificationPage from "./pages/NotificationPage";
import PricingPage from "./pages/PricingPage";
import HomePageEight from "./pages/HomePageEight";
import HomePageNine from "./pages/HomePageNine";
import HomePageTen from "./pages/HomePageTen";
import HomePageEleven from "./pages/HomePageEleven";

import AccessDeniedPage from "./pages/AccessDeniedPage";

import ApplicationsPage from "./pages/ApplicationsPage";
import CategoriesPage from "./pages/CategoriesPage";
import PositionManagementPage from "./pages/PositionManagementPage";
import VenueManagementPage from "./pages/VenueManagementPage";
import VenueDetailPage from "./pages/VenueDetailPage";
import AddVenuePage from "./pages/AddVenuePage";
import EditVenuePage from "./pages/EditVenuePage";
import PrivateRoute from "./helper/PrivateRoute";
import SubscriptionRoute from "./helper/SubscriptionRoute";
import EmployeeManagementPage from "./pages/EmployeeManagementPage";
import config from "./config";

const APP_TYPE = process.env.REACT_APP_APP_TYPE || 'dev';

function App() {
  const updateSettings = () => {
    fetch(`${config.API_BASE_URL}/api/settings`)
      .then(res => res.json())
      .then(data => {
        if (data.site_title) {
          window.__SITE_TITLE__ = data.site_title;
          window.dispatchEvent(new Event('titleUpdateNeeded'));
        }
        if (data.favicon_url) {
          const favicons = document.querySelectorAll("link[rel*='icon']");
          favicons.forEach(favicon => {
            const resolvedFaviconUrl = data.favicon_url.startsWith('http') || data.favicon_url.startsWith('data:') ? data.favicon_url : `${config.API_BASE_URL}${data.favicon_url.startsWith('/') ? '' : '/'}${data.favicon_url}`;
            favicon.href = resolvedFaviconUrl;
            favicon.removeAttribute('sizes');
          });
          
          // Fallback if no link exists
          if (favicons.length === 0) {
            const newFavicon = document.createElement('link');
            newFavicon.rel = 'icon';
            newFavicon.href = data.favicon_url.startsWith('http') || data.favicon_url.startsWith('data:') ? data.favicon_url : `${config.API_BASE_URL}${data.favicon_url.startsWith('/') ? '' : '/'}${data.favicon_url}`;
            document.head.appendChild(newFavicon);
          }
        }
        if (data.theme_color) {
          applyTheme(data.theme_color);
        }
      })
      .catch(err => console.error('Failed to fetch settings:', err));
  };

  useEffect(() => {
    updateSettings();
    window.addEventListener('settingsChanged', updateSettings);
    return () => {
      window.removeEventListener('settingsChanged', updateSettings);
    };
  }, []);

  return (
    <ApplicationProvider>
      <ToastContainer />
      <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <RouteScrollToTop />
      <Routes>
        <Route exact path='/' element={<LandingPage />} />

        {/* Authentication */}
        <Route exact path='/sign-in' element={<SignInPage />} />
        {APP_TYPE !== 'admin' ? (
            <Route exact path='/sign-up' element={<SignUpPage />} />
        ) : (
            <Route exact path='/sign-up' element={<Navigate to="/sign-in" replace />} />
        )}
        <Route exact path='/forgot-password' element={<ForgotPasswordPage />} />
        <Route exact path='/reset-password/:token' element={<ResetPasswordPage />} />
        <Route exact path='/tos' element={<TosPage />} />
        <Route exact path='/privacy' element={<BusinessPrivacyPage />} />
        <Route exact path='/business-privacy' element={<BusinessPrivacyPage />} />
        <Route exact path='/terms-and-conditions' element={<PublicTosPage />} />
        <Route exact path='/privacy-policy' element={<PublicPrivacyPage />} />
        <Route exact path='/contact' element={<ContactUsPage />} />

        {/* Generic Authenticated Routes */}
        <Route element={<PrivateRoute />}>
            <Route exact path='/applications' element={<ApplicationsPage />} />
            <Route exact path='/applications/:tabId' element={<ApplicationsPage />} />
            <Route exact path='/all-venues' element={<Navigate to="/applications/all-venues" replace />} />
            <Route exact path='/reservations' element={<ReservationsPage />} />
            <Route exact path='/view-profile/:id?' element={<ViewProfilePage />} />
        </Route>

        {/* Calendar and task management for business accounts and authorized staff */}
        <Route element={<PrivateRoute allowedRoles={['venue_owner', 'employee', 'admin']} />}>
            <Route exact path='/calendar-main' element={<CalendarMainPage />} />
            <Route exact path='/calendar' element={<CalendarMainPage />} />
        </Route>

        {/* Content Permissions */}
        <Route element={<PrivateRoute requiredPermission="content" />}>
            <Route exact path='/categories' element={<CategoriesPage />} />
            <Route exact path='/content-moderation' element={<ContentModerationPage />} />
            <Route exact path='/mobile-content' element={<MobileContentPage />} />
            <Route exact path='/maps' element={<MapsPage />} />
        </Route>

        {/* Venue Owner & Admin Routes - Protected by Subscription (Admin bypassed) */}
        {(APP_TYPE === 'business' || APP_TYPE === 'admin' || APP_TYPE === 'dev') && (
        <Route element={<PrivateRoute />}>
            <Route element={<SubscriptionRoute />}>
                {(APP_TYPE === 'business' || APP_TYPE === 'dev') && (
                    <Route exact path='/my-venues' element={<VenueManagementPage />} />
                )}
                {(APP_TYPE === 'business' || APP_TYPE === 'dev') && (
                    <Route exact path='/add-venue' element={<AddVenuePage />} />
                )}
                <Route exact path='/venue/:id' element={<VenueDetailPage />} />
                <Route exact path='/edit-venue/:id' element={<EditVenuePage />} />
            </Route>
        </Route>
        )}

        {/* Venue Permissions (Admin) */}
        {(APP_TYPE === 'admin' || APP_TYPE === 'dev') && (
        <Route element={<PrivateRoute requiredPermission="venues" />}>
            <Route exact path='/venue-verification' element={<VenueVerificationPage />} />
        </Route>
        )}

        {/* Subscription Permissions */}
        {(APP_TYPE === 'business' || APP_TYPE === 'dev') && (
        <Route element={<PrivateRoute requiredPermission="subscriptions" />}>
            <Route exact path='/subscription-control' element={<SubscriptionControlPage />} />
        </Route>
        )}
        
        {/* Pricing - Accessible to all authenticated users */}
        {(APP_TYPE === 'business' || APP_TYPE === 'dev') && (
        <Route element={<PrivateRoute />}>
             <Route exact path='/pricing' element={<PricingPage />} />
        </Route>
        )}

        {/* User Management Permissions */}
        <Route element={<PrivateRoute requiredPermission="users" />}>
             <Route exact path='/user-management' element={<UserManagementPage />} />

             
             <Route exact path='/view-details' element={<ViewDetailsPage />} />
        </Route>


        {/* Admin Access Permissions */}
        {(APP_TYPE === 'admin' || APP_TYPE === 'dev') && (
        <Route element={<PrivateRoute requiredPermission="support" />}>
            <Route exact path='/support-tickets' element={<SupportTicketsPage />} />
        </Route>
        )}

        {(APP_TYPE === 'admin' || APP_TYPE === 'dev') && (
        <Route element={<PrivateRoute requiredPermission="admin_access" />}>
            <Route exact path='/control-tower' element={<ControlTowerPage />} />
            <Route exact path='/audit-logs' element={<AuditLogsPage />} />
            <Route exact path='/contact-messages' element={<ContactMessagesPage />} />
            <Route exact path='/employee-management' element={<EmployeeManagementPage />} />
            <Route exact path='/position-management' element={<PositionManagementPage />} />


            {/* Settings */}
            <Route exact path='/notification' element={<NotificationPage />} />
            <Route exact path='/theme' element={<ThemePage />} />
        </Route>
        )}

        {/* Public or Common Routes */}
        <Route element={<PrivateRoute requiredPermission="dashboard" />}>
            <Route exact path='/index-1' element={<HomePageOne />} />
            <Route exact path='/index-2' element={<HomePageTwo />} />
            <Route exact path='/index-3' element={<HomePageThree />} />
            <Route exact path='/index-4' element={<HomePageFour />} />
            <Route exact path='/index-5' element={<HomePageFive />} />
            <Route exact path='/index-6' element={<HomePageSix />} />
            <Route exact path='/index-7' element={<HomePageSeven />} />
            <Route exact path='/index-8' element={<HomePageEight />} />
            <Route exact path='/index-9' element={<HomePageNine />} />
            <Route exact path='/index-10' element={<HomePageTen />} />
            <Route exact path='/index-11' element={<HomePageEleven />} />
        </Route>
        
        {/* System Pages */}
        <Route exact path='/access-denied' element={<AccessDeniedPage />} />

        <Route exact path='/error' element={<ErrorPage />} />
        <Route exact path='*' element={<ErrorPage />} />
      </Routes>
    </BrowserRouter>
    </ApplicationProvider>
  );
}

export default App;
